import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Bug } from "lucide-react";
import Header from "@/components/Header";
import { Card, TextArea, TextInput, Field } from "@/components/ui-kit";
import { StickySubmit } from "./TemperatureForm";
import { createRecord, updateRecord } from "@/lib/api";
import { getUser } from "@/lib/auth";

export default function PestForm() {
  const navigate = useNavigate();
  const editRecord = useLocation().state?.editRecord;
  const today  = new Date().toISOString().slice(0, 10);
  const recordDate = editRecord?.date || today;
  const signer = getUser()?.username || "";
  const isAdmin = getUser()?.rol === "admin";
  const [date, setDate]                 = useState(recordDate);
  const [result, setResult]             = useState(editRecord?.visual_result || "sense_indici");
  const [measure, setMeasure]           = useState(editRecord?.measure_taken || "");
  const [pestCompany, setPestCompany]   = useState(editRecord?.pest_company || "");
  const [deliveryNote, setDeliveryNote] = useState(editRecord?.delivery_note || "");
  const [submitting, setSubmitting]     = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { date, visual_result: result,
        measure_taken: measure, pest_company: pestCompany, delivery_note: deliveryNote, signer };
      if (editRecord) await updateRecord("pest", editRecord.id, payload);
      else await createRecord("pest", payload);
      toast.success(editRecord ? "Inspecció modificada" : "Inspeccio registrada");
      navigate("/history?type=pest");
    } catch { toast.error("Error desant el registre"); }
    finally   { setSubmitting(false); }
  };

  return (
    <>
      <Header title="Plagues" />
      <div className="mx-auto max-w-2xl px-4 pb-32 pt-6 sm:px-6 sm:pt-10">
        <div className="animate-fade-up">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest text-white"><Bug size={22} /></div>
            <div>
              <div className="label-eyebrow text-forest">Registre diari</div>
              <h2 className="font-display text-2xl font-bold text-ink sm:text-3xl">Control de plagues</h2>
            </div>
          </div>
          <p className="mt-3 text-sm text-ink-soft">Inspeccio visual diaria. Si veus indicis, avisa el Mercat i l'empresa de plagues.</p>
        </div>
        <div className="mt-6 space-y-4">
          {(isAdmin || editRecord) && (
            <Card>
              <div className="label-eyebrow mb-2">Data del registre {editRecord ? "" : "(admin)"}</div>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-14 w-full rounded-xl border border-line bg-white px-4 text-base text-ink outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20" />
            </Card>
          )}
          <Card>
            <div className="label-eyebrow mb-3">Resultat de la revisio</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                { v: "sense_indici",  label: "Cap indici",       desc: "Tot net, sense rastres", tone: "ok",    testid: "pest-result-clean" },
                { v: "indici_detectat", label: "Indici detectat", desc: "Cal mesura correctora", tone: "alert", testid: "pest-result-detected" },
              ].map(({ v, label, desc, tone, testid }) => {
                const active = result === v;
                const activeClass = tone === "ok" ? "border-forest bg-forest-bg" : "border-ember bg-ember-bg";
                return (
                  <button key={v} type="button" data-testid={testid} onClick={() => setResult(v)}
                    className={"rounded-xl border-2 p-4 text-left transition-all " + (active ? activeClass : "border-line bg-white hover:border-ink/30")}>
                    <div className={"font-display text-lg font-bold " + (active && tone === "ok" ? "text-forest" : active ? "text-ember" : "text-ink")}>{label}</div>
                    <div className="text-xs text-ink-soft">{desc}</div>
                  </button>
                );
              })}
            </div>
          </Card>
          {result === "indici_detectat" && (
            <Card className="animate-fade-up">
              <Field label="Mesura adoptada">
                <TextArea testid="pest-measure" placeholder="Que s'ha fet? Trampes colllocades, neteja extra..."
                  value={measure} onChange={(e) => setMeasure(e.target.value)} />
              </Field>
            </Card>
          )}
          <Card>
            <div className="space-y-4">
              <Field label="Empresa plagues (opcional)"><TextInput testid="pest-company" placeholder="Nom de l'empresa" value={pestCompany} onChange={(e) => setPestCompany(e.target.value)} /></Field>
              <Field label="Albara (opcional)"><TextInput testid="pest-delivery-note" placeholder="Num. d'albara" value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} /></Field>
            </div>
          </Card>
        </div>
      </div>
      <StickySubmit onClick={submit} disabled={submitting} label={submitting ? "Desant..." : editRecord ? "Desar canvis" : "Desar registre"} />
    </>
  );
}
